import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import AvailabilityUtils from "../../../../utils/availability/AvailabilityUtils";
import { Calendar, Clock, Building2 } from "lucide-react";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import { Button } from "../../../../shadcn/ui/button";
import { cn } from "../../../../lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../shadcn/ui/tooltip";
import { ChevronUp, ChevronDown } from "lucide-react";
import SmartDatePicker from "../../../../components/SmartDatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../shadcn/ui/popover";
import { Calendar as ShadcnCalendar } from "../../../../shadcn/ui/calendar";
import logger from "../../../../utils/logger";

// Stable empty array reference to prevent infinite re-render loops from default destructuring values
const EMPTY_ARRAY = [];

// Date formatting helper (no external dependency)
const formatDate = (date, formatStr) => {
  if (!date) return "";
  const d = new Date(date);
  if (formatStr === "LLL dd, y") {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  if (formatStr === "MMM dd, yyyy") {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  if (formatStr === "HH:mm") {
    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");
    return `${h}:${m}`;
  }
  return d.toLocaleDateString();
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * BookingTypeSelector - Shows appropriate booking selector based on duration type
 *
 * Supports:
 * - HOURLY: Time selector (start time + end time)
 * - DAILY: Date range selector
 * - WEEKLY: Date range selector
 * - MONTHLY: Date range selector
 */
const BookingTypeSelector = ({
  durationType = "HOURLY", // HOURLY, DAILY, WEEKLY, MONTHLY
  selectedDate, // Initial selected date
  availableTimeSlots = [], // For HOURLY bookings - used for reference but we show all slots
  space = null, // Space object with availability windows
  minimumDuration = 1, // Minimum duration (in hours for HOURLY, days for DAILY, etc.)
  maximumDuration = 8, // Maximum duration
  bufferTimeInMinutes = 0, // Buffer time after bookings
  existingBookings = [], // Existing bookings for conflict checking
  availabilityInstanceIds = EMPTY_ARRAY, // Instance IDs from availability endpoint
  spaceId = null, // Space ID for filtering bookings
  timeFormat = "12h", // 12h or 24h
  onSelectionChange, // Callback with { startTime, endTime, startDate, endDate }
  onDateChange, // Callback for date changes (used for HOURLY)
  disabled = false,
  instances = EMPTY_ARRAY,
  instanceDayStatusFn = null,
  initialSelection = null,
}) => {
  // Stable ref for onSelectionChange to avoid infinite loops in useEffects
  const onSelectionChangeRef = React.useRef(onSelectionChange);
  React.useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // State for HOURLY bookings
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);

  // State for date range bookings (DAILY, WEEKLY, HOURLY)
  const [dateRange, setDateRange] = useState(() => {
    if (initialSelection?.window?.startTime) {
      const start = new Date(initialSelection.window.startTime);
      const end = new Date(initialSelection.window.endTime || initialSelection.window.startTime);
      if (!Number.isNaN(start.getTime())) {
        return { from: start, to: end };
      }
    }
    if (selectedDate) {
      return { from: selectedDate, to: selectedDate };
    }
    return { from: null, to: null };
  });

  // Effective selected date: prioritize chosen dateRange.from for HOURLY, fallback to selectedDate prop
  const effectiveSelectedDate = useMemo(() => {
    if (durationType === "HOURLY" && dateRange?.from) {
      return dateRange.from;
    }
    return selectedDate;
  }, [durationType, dateRange?.from, selectedDate]);
  const [startDateTime, setStartDateTime] = useState(null); // For DAILY bookings with time
  const [endDateTime, setEndDateTime] = useState(null);
  const [datePickerValue, setDatePickerValue] = useState(null);
  const [hasManualTimes, setHasManualTimes] = useState(false); // Track if user manually set times

  // State for HOURLY calendar
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMonthlyCalendarOpen, setIsMonthlyCalendarOpen] = useState(false);
  const [isInstancePreviewOpen, setIsInstancePreviewOpen] = useState(false);

  // State for MONTHLY bookings
  const [monthlyStartDate, setMonthlyStartDate] = useState(null);
  const [monthlyMonthCount, setMonthlyMonthCount] = useState(minimumDuration || 1);

  // Refresh trigger to force recalculation of available dates as time passes
  const [dateRefreshTrigger, setDateRefreshTrigger] = useState(0);
  const startOfToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const minSelectableDate = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // For daily, weekly, and monthly bookings, if it's past 12 PM (noon), the first available day is tomorrow.
    if ((durationType === "DAILY" || durationType === "WEEKLY" || durationType === "MONTHLY") && now.getHours() >= 12) {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    }

    return today;
  }, [durationType, dateRefreshTrigger]);

  const maxSelectableDate = useMemo(() => addDays(startOfToday, 90), [startOfToday]);

  // Ref for auto-scrolling to 8 AM in time slot list
  const startTimeContainerRef = React.useRef(null);

  // Sync dateRange when selectedDate prop changes
  useEffect(() => {
    if (selectedDate) {
      setDateRange(prev => {
        // Only update if the date actually changed to avoid cycles
        if (prev.from && prev.from.toDateString() === selectedDate.toDateString()) {
          return prev;
        }
        return { from: selectedDate, to: selectedDate };
      });
    }
  }, [selectedDate]);

  // Refresh available dates every minute to handle time passing (edge case for today's date)
  useEffect(() => {
    const interval = setInterval(() => {
      setDateRefreshTrigger(prev => prev + 1);
    }, 60000); // Refresh every 60 seconds

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to 8 AM slot when date is selected or changes
  useEffect(() => {
    if (!startTimeContainerRef.current || !dateRange.from || durationType !== "HOURLY") return;

    // Wait for next tick to ensure DOM is updated
    setTimeout(() => {
      const container = startTimeContainerRef.current;
      if (!container) return;

      // Find the 8 AM button (08:00 in 24h format or 8:00 AM in 12h)
      const eightAmButton = Array.from(container.querySelectorAll("button")).find(button => {
        const text = button.textContent;
        return text.includes("8:00 AM") || text.includes("08:00");
      });

      if (eightAmButton) {
        // Scroll the button into view
        eightAmButton.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
      }
    }, 100);
  }, [dateRange.from, durationType]);

  const filteredBookings = useMemo(() => {
    if (!existingBookings || existingBookings.length === 0) return [];
    if (!spaceId) return existingBookings;

    return existingBookings.filter(booking => {
      const bookingSpaceId = booking?.spaceId ?? booking?.space?.id;
      if (bookingSpaceId == null) return true;
      try {
        return bookingSpaceId.toString() === spaceId.toString();
      } catch {
        return bookingSpaceId === spaceId;
      }
    });
  }, [existingBookings, spaceId]);

  const availableDayNames = useMemo(() => {
    const set = new Set();

    if (Array.isArray(space?.availableSlots) && space.availableSlots.length > 0) {
      space.availableSlots.forEach(slot => {
        if (slot?.day) {
          set.add(slot.day.toUpperCase());
        }
      });
    }

    if (set.size === 0 && Array.isArray(availableTimeSlots) && availableTimeSlots.length > 0) {
      availableTimeSlots.forEach(slot => {
        if (slot?.day) {
          set.add(String(slot.day).toUpperCase());
          return;
        }

        const sourceDate = slot?.startTime || slot?.dateTime || slot?.rawStartTime;
        if (!sourceDate) return;

        const dateObj = sourceDate instanceof Date ? sourceDate : new Date(sourceDate);
        if (!Number.isNaN(dateObj?.getTime())) {
          const dayName = dateObj.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
          set.add(dayName);
        }
      });
    }

    return set;
  }, [space?.availableSlots, availableTimeSlots]);

  const hasAvailabilityConstraints = availableDayNames && availableDayNames.size > 0;

  const isDateWithinAvailability = useCallback(
    date => {
      if (!hasAvailabilityConstraints) return true;
      const dayName = date.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase();
      return availableDayNames.has(dayName);
    },
    [availableDayNames, hasAvailabilityConstraints]
  );

  const calendarModifiersStyles = useMemo(() => {
    const styles = {
      booked: {
        backgroundColor: "#fee2e2",
        color: "#dc2626",
        fontWeight: "bold",
      },
      partial: {
        backgroundColor: "#ffedd5",
        color: "#9a3412",
        fontWeight: "bold",
      },
      available: {
        backgroundColor: "#dcfce7",
        color: "#166534",
        fontWeight: "bold",
      },
    };

    return styles;
  }, []);

  // Format time helper
  const formatTime = useCallback(
    date => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "—";
      const h = date.getHours();
      const m = date.getMinutes().toString().padStart(2, "0");
      if (timeFormat === "24h") {
        return `${h.toString().padStart(2, "0")}:${m}`;
      } else {
        const period = h >= 12 ? "PM" : "AM";
        const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${displayHour}:${m} ${period}`;
      }
    },
    [timeFormat]
  );

  // Generate all 24-hour time slots in 30-minute intervals (48 slots total)
  const generateAllTimeSlots = useCallback(() => {
    if (!effectiveSelectedDate) return [];

    const slots = [];
    const baseDate = effectiveSelectedDate;

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 60) {
        const slotTime = new Date(baseDate);
        slotTime.setHours(hour, minute, 0, 0);

        slots.push({
          time: formatTime(slotTime),
          dateTime: slotTime,
          label: formatTime(slotTime),
          hour,
          minute,
        });
      }
    }

    return slots;
  }, [effectiveSelectedDate, formatTime]);

  // Restore initialSelection (e.g. after login redirect)
  const appliedInitialSelectionKeyRef = useRef(null);
  useEffect(() => {
    if (!initialSelection?.window) return;
    const { startTime, endTime, startDate, endDate } = initialSelection.window;
    if (!startTime || !endTime) return;

    const start = new Date(startTime);
    const end = new Date(endTime);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;

    const key = `${start.toISOString()}|${end.toISOString()}|${durationType}`;
    if (appliedInitialSelectionKeyRef.current === key) return;
    appliedInitialSelectionKeyRef.current = key;

    logger.log("[BookingTypeSelector] Restoring initialSelection:", { start, end, durationType });

    if (durationType === "HOURLY") {
      const formattedStart = formatTime(start);
      const formattedEnd = formatTime(end);
      setSelectedStartTime(formattedStart);
      setSelectedEndTime(formattedEnd);
      setDateRange({ from: start, to: end });
      if (onDateChange) {
        onDateChange(start);
      }
    } else if (durationType === "DAILY" || durationType === "WEEKLY") {
      const s = startDate ? new Date(startDate) : start;
      const e = endDate ? new Date(endDate) : end;
      setDateRange({ from: s, to: e });
      setStartDateTime(s);
      setEndDateTime(e);
      setDatePickerValue({ startDate: s, endDate: e });
      setHasManualTimes(true);
      if (onDateChange) {
        onDateChange(s);
      }
    } else if (durationType === "MONTHLY") {
      const s = startDate ? new Date(startDate) : start;
      setMonthlyStartDate(s);
      if (onDateChange) {
        onDateChange(s);
      }
    }
  }, [initialSelection, durationType, formatTime, onDateChange]);

  // Check if a time slot is available (within availability window and not booked)
  const isSlotAvailable = useCallback(
    (slotDateTime, isStartTime = true) => {
      if (!effectiveSelectedDate) return false;

      // Check if slot is in the past (for today's date)
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDay = new Date(effectiveSelectedDate.getFullYear(), effectiveSelectedDate.getMonth(), effectiveSelectedDate.getDate());

      // If selected date is today, disable slots that are in the past
      if (selectedDay.getTime() === today.getTime()) {
        if (slotDateTime.getTime() < now.getTime()) {
          return false;
        }
      }

      // Get day name for checking availability
      const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
      const dayName = dayNames[effectiveSelectedDate.getDay()];

      // Check against space availability windows if space is provided
      if (space && space.availableSlots && space.availableSlots.length > 0) {
        const dayAvailability = space.availableSlots.filter(slot => slot && slot.day === dayName);

        if (dayAvailability.length === 0) return false;

        // Check if slot falls within any availability window for this day
        const slotHour = slotDateTime.getHours();
        const slotMinute = slotDateTime.getMinutes();
        const slotTotalMinutes = slotHour * 60 + slotMinute;

        const isInAvailabilityWindow = dayAvailability.some(avail => {
          if (!avail || !avail.startTime || !avail.endTime) return false;

          const [startHours, startMinutes] = avail.startTime.split(":").map(Number);
          const [endHours, endMinutes] = avail.endTime.split(":").map(Number);
          const startTotalMinutes = startHours * 60 + startMinutes;
          const endTotalMinutes = endHours * 60 + endMinutes;

          if (isStartTime) {
            // For start time, slot must be at or after start and before end
            return slotTotalMinutes >= startTotalMinutes && slotTotalMinutes < endTotalMinutes;
          } else {
            // For end time, slot must be after start and at or before end
            return slotTotalMinutes > startTotalMinutes && slotTotalMinutes <= endTotalMinutes;
          }
        });

        if (!isInAvailabilityWindow) return false;
      } else if (availableTimeSlots && availableTimeSlots.length > 0) {
        // Fallback to availableTimeSlots if space not provided
        const isInAvailabilityWindow = availableTimeSlots.some(availSlot => {
          // If the slot itself is marked as unavailable, skip it
          if (availSlot.available === false) return false;

          const slotTime = slotDateTime.getTime();
          const availStart = availSlot.startTime ? new Date(availSlot.startTime).getTime() : 0;
          const availEnd = availSlot.endTime ? new Date(availSlot.endTime).getTime() : 0;

          if (isStartTime) {
            return slotTime >= availStart && slotTime < availEnd;
          } else {
            return slotTime > availStart && slotTime <= availEnd;
          }
        });

        if (!isInAvailabilityWindow) return false;
      } else {
        // No availability data - all slots disabled
        logger.log("[BookingTypeSelector] isSlotAvailable - NO AVAILABILITY DATA:", {
          slotTime: slotDateTime.toISOString(),
          hasSpace: !!space,
          hasSpaceAvailableSlots: !!(space && space.availableSlots),
          spaceAvailableSlotsLength: space?.availableSlots?.length,
          hasAvailableTimeSlots: !!availableTimeSlots,
          availableTimeSlotsLength: availableTimeSlots?.length,
        });
        return false;
      }

      // Check for conflicts with existing bookings
      let bookedCount = 0;
      let totalInstances = 1;

      if (filteredBookings.length > 0) {
        totalInstances =
          space?.totalInstances ||
          space?.numberOfInstances ||
          instances?.length ||
          availabilityInstanceIds?.length ||
          1;

        const slotTime = slotDateTime.getTime();
        const bufferMs = bufferTimeInMinutes * 60 * 1000;

        // Count how many instances are busy at this time
        const busyInstanceIds = new Set();

        filteredBookings.forEach(booking => {
          const bookingStart = new Date(booking.startTime).getTime();
          const bookingEnd = new Date(booking.endTime).getTime();
          const bookingEndWithBuffer = bookingEnd + bufferMs;

          if (slotTime < bookingEndWithBuffer && slotTime >= bookingStart) {
            if (booking.spaceInstanceId) {
              busyInstanceIds.add(booking.spaceInstanceId);
            } else {
              if (totalInstances > 1) {
                busyInstanceIds.add(`legacy_${booking.id || Math.random()}`);
              } else {
                busyInstanceIds.add("single_instance");
              }
            }
          }
        });

        bookedCount = busyInstanceIds.size;
      }

      // Important: Return available: true if the slot is WITHIN operating hours,
      // even if it's fully booked. This allows the UI to show it as a red/orange block
      // rather than hiding it entirely.
      return {
        available: true,
        totalInstances,
        bookedCount,
      };
    },
    [effectiveSelectedDate, space, availableTimeSlots, filteredBookings, bufferTimeInMinutes]
  );

  // Get all time options for HOURLY bookings (only show available slots)
  const getAllTimeOptions = useCallback(() => {
    const allSlots = generateAllTimeSlots();

    // Check if selected date is today
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDay = effectiveSelectedDate
      ? new Date(effectiveSelectedDate.getFullYear(), effectiveSelectedDate.getMonth(), effectiveSelectedDate.getDate())
      : null;
    const isToday = selectedDay && selectedDay.getTime() === today.getTime();

    const filteredSlots = allSlots
      .map(slot => {
        const availabilityInfo = isSlotAvailable(slot.dateTime, true);
        // Handle both object return (new) and boolean return (legacy/fallback)
        const isAvail = typeof availabilityInfo === "object" ? availabilityInfo.available : availabilityInfo;

        return {
          ...slot,
          available: isAvail,
          availabilityDetails: typeof availabilityInfo === "object" ? availabilityInfo : null,
        };
      })
      .filter(slot => {
        // If today, hide slots that are in the past
        if (isToday && slot.dateTime.getTime() < now.getTime()) {
          return false;
        }
        // Only show slots that are actually available (within operating hours and not fully booked)
        return slot.available;
      });

    return filteredSlots;
  }, [generateAllTimeSlots, isSlotAvailable, effectiveSelectedDate]);

  // Get end time options based on selected start time (HOURLY)
  const getEndTimeOptions = useCallback(() => {
    if (!selectedStartTime) return [];

    const allSlots = generateAllTimeSlots();
    const startSlotIndex = allSlots.findIndex(slot => slot.time === selectedStartTime);
    if (startSlotIndex === -1) return [];

    // Check if selected date is today
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDay = effectiveSelectedDate
      ? new Date(effectiveSelectedDate.getFullYear(), effectiveSelectedDate.getMonth(), effectiveSelectedDate.getDate())
      : null;
    const isToday = selectedDay && selectedDay.getTime() === today.getTime();

    const endOptions = [];
    const minDurationMinutes = minimumDuration * 60;
    const maxDurationMinutes = maximumDuration * 60;

    // Find the start slot's dateTime
    const startSlot = allSlots[startSlotIndex];
    const startDateTime = startSlot.dateTime;

    for (let i = startSlotIndex + 1; i < allSlots.length; i++) {
      const slot = allSlots[i];

      // If today, hide slots that are in the past
      if (isToday && slot.dateTime.getTime() < now.getTime()) {
        continue;
      }

      const durationMinutes = (slot.dateTime.getTime() - startDateTime.getTime()) / (1000 * 60);

      if (durationMinutes >= minDurationMinutes && durationMinutes <= maxDurationMinutes) {
        const durationHours = Math.floor(durationMinutes / 60);
        const durationLabel = `${durationHours}h`;

        const availabilityInfo = isSlotAvailable(slot.dateTime, false);
        const isAvail = typeof availabilityInfo === "object" ? availabilityInfo.available : availabilityInfo;

        if (isAvail) {
          endOptions.push({
            time: slot.time,
            dateTime: slot.dateTime,
            label: slot.label,
            duration: durationMinutes,
            durationLabel,
            available: true,
          });
        }
      }

      if (durationMinutes >= maxDurationMinutes) break;
    }

    return endOptions;
  }, [selectedStartTime, generateAllTimeSlots, minimumDuration, maximumDuration, isSlotAvailable, effectiveSelectedDate]);

  // Check for booking conflicts in a date range
  const checkRangeConflicts = useCallback(
    (startDate, endDate) => {
      if (!filteredBookings.length || !spaceId) return null;

      const totalInstances =
        space?.totalInstances || space?.numberOfInstances || instances?.length || availabilityInstanceIds?.length || 1;

      // Check if any booking falls within the range
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      // For range bookings, we need to check each day in the range
      const conflicts = [];
      const currentDate = new Date(start);

      while (currentDate <= end) {
        const checkDateStart = new Date(currentDate);
        checkDateStart.setHours(0, 0, 0, 0);
        const checkDateEnd = new Date(currentDate);
        checkDateEnd.setHours(23, 59, 59, 999);

        // Check which bookings overlap with this specific day
        const dayBookings = filteredBookings.filter(booking => {
          const bookingStart = new Date(booking.startTime).getTime();
          const bookingEnd = new Date(booking.endTime).getTime();
          return bookingStart < checkDateEnd.getTime() && bookingEnd > checkDateStart.getTime();
        });

        if (dayBookings.length > 0) {
          // Count unique busy instances
          const busyInstanceIds = new Set();
          dayBookings.forEach(booking => {
            if (booking.spaceInstanceId) {
              busyInstanceIds.add(booking.spaceInstanceId);
            } else {
              // Legacy/General booking - count as 1 or use dummy ID
              busyInstanceIds.add(`legacy_${booking.id || Math.random()}`);
            }
          });

          // Only a conflict if ALL instances are busy on this day
          if (busyInstanceIds.size >= totalInstances) {
            conflicts.push({
              date: new Date(currentDate),
              bookings: dayBookings,
            });
          }
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      return conflicts.length > 0 ? conflicts : null;
    },
    [filteredBookings, spaceId, space, instances, availabilityInstanceIds]
  );

  // Get dates that are FULLY booked (all instances taken)
  const getBookedDates = useCallback(() => {
    if (!filteredBookings.length) return new Set();

    const totalInstances =
      space?.totalInstances || space?.numberOfInstances || instances?.length || availabilityInstanceIds?.length || 1;

    const bookedDates = new Set();
    const dateCounts = new Map(); // Date string -> Set of booking IDs or instance IDs

    filteredBookings.forEach(booking => {
      if (!booking.startTime || !booking.endTime) return;

      const start = new Date(booking.startTime);
      const end = new Date(booking.endTime);
      const current = new Date(start);
      current.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      // Add all dates within the booking range
      while (current <= end) {
        const dateStr = current.toDateString();
        if (!dateCounts.has(dateStr)) {
          dateCounts.set(dateStr, new Set());
        }

        // Count this booking for this date
        // Use instance ID if available, otherwise booking ID to avoid double counting same-instance overlaps
        const identifier = booking.spaceInstanceId || booking.id || Math.random();
        dateCounts.get(dateStr).add(identifier);

        current.setDate(current.getDate() + 1);
      }
    });

    // Only add to bookedDates if count >= totalInstances
    dateCounts.forEach((busySet, dateStr) => {
      if (busySet.size >= totalInstances) {
        bookedDates.add(dateStr);
      }
    });

    return bookedDates;
  }, [filteredBookings, space, instances, availabilityInstanceIds]);

  const bookedDatesSet = useMemo(() => getBookedDates(), [getBookedDates]);

  // Get booking info for a specific date
  const getBookingsForDate = useCallback(
    date => {
      if (!filteredBookings.length) return [];

      return filteredBookings.filter(booking => {
        if (!booking.startTime || !booking.endTime) return false;

        const bookingStart = new Date(booking.startTime).getTime();
        const bookingEnd = new Date(booking.endTime).getTime();
        const checkDateStart = new Date(date);
        checkDateStart.setHours(0, 0, 0, 0);
        const checkDateEnd = new Date(date);
        checkDateEnd.setHours(23, 59, 59, 999);

        // Check if booking overlaps with this date
        return bookingStart < checkDateEnd.getTime() && bookingEnd > checkDateStart.getTime();
      });
    },
    [filteredBookings]
  );

  // Calculate appropriate start time to prevent booking in the past
  const calculateSafeStartTime = useCallback((selectedDate, categoryName = "WORK") => {
    const now = new Date();
    const selectedDateTime = new Date(selectedDate);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDay = new Date(
      selectedDateTime.getFullYear(),
      selectedDateTime.getMonth(),
      selectedDateTime.getDate()
    );

    // If selected date is not today, use default category times
    const constraints = AvailabilityUtils.getTimeConstraints(categoryName, "HOURLY");
    const [defaultStartHour] = constraints.startTime.split(":").map(Number);
    const [defaultEndHour] = constraints.endTime.split(":").map(Number);

    if (selectedDay.getTime() !== today.getTime()) {
      return defaultStartHour;
    }

    // Selected date is today - calculate safe start time
    const currentHour = now.getHours();

    if (currentHour < defaultStartHour) {
      return defaultStartHour;
    } else {
      // Current time + 1 hour, but not past a reasonable end time
      // Use defaultEndHour as the cap
      const nextHour = Math.min(currentHour + 1, defaultEndHour);
      return nextHour;
    }
  }, []);

  // Helper function to check if a time range conflicts with bookings (matches final validation logic)
  const checkTimeRangeConflict = useCallback(
    (rangeStart, rangeEnd) => {
      if (!rangeStart || !rangeEnd || filteredBookings.length === 0) return false;

      const rangeStartTime = rangeStart.getTime();
      const rangeEndTime = rangeEnd.getTime();
      const bufferMs = bufferTimeInMinutes * 60 * 1000;

      const totalInstances =
        space?.totalInstances || space?.numberOfInstances || instances?.length || availabilityInstanceIds?.length || 1;

      // Find all overlapping bookings
      const overlappingBookings = filteredBookings.filter(booking => {
        if (!booking.startTime || !booking.endTime) return false;

        const bookingStart = new Date(booking.startTime).getTime();
        const bookingEnd = new Date(booking.endTime).getTime();

        // Apply buffer time to booking
        const bookingEndWithBuffer = bookingEnd + bufferMs;
        const bookingStartWithBuffer = bookingStart - bufferMs;

        // Overlap occurs if: range starts before booking ends (with buffer) AND range ends after booking starts (with buffer)
        return rangeStartTime < bookingEndWithBuffer && rangeEndTime > bookingStartWithBuffer;
      });

      // If no overlaps, no conflict
      if (overlappingBookings.length === 0) return false;

      // If we have overlaps, check if we've exceeded capacity

      // Count unique instances that are busy
      const busyInstanceIds = new Set();

      overlappingBookings.forEach(booking => {
        if (booking.spaceInstanceId) {
          busyInstanceIds.add(booking.spaceInstanceId);
        } else {
          // Logic for bookings without instance ID
          if (totalInstances > 1) {
            busyInstanceIds.add(`legacy_${booking.id || Math.random()}`);
          } else {
            busyInstanceIds.add("single_instance");
          }
        }
      });

      return busyInstanceIds.size >= totalInstances;
    },
    [filteredBookings, bufferTimeInMinutes, space, instances, availabilityInstanceIds]
  );

  // Get available dates based on space availability constraints and actual slot availability
  const getAvailableDates = useCallback(() => {
    if (!space || !space.availableSlots || space.availableSlots.length === 0) return new Set();

    const availableDates = new Set();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const now = new Date();

    // Check availability for the next 90 days
    for (let i = 0; i < 90; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() + i);

      const dayName = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"][
        checkDate.getDay()
      ];

      // Check if this day has any availability slots
      const dayAvailability = space.availableSlots.filter(slot => slot.day === dayName);

      if (dayAvailability.length === 0) continue;

      // Check if there are actually any available time slots on this date
      // (accounting for past time, existing bookings, and buffer time)
      const isToday = checkDate.getTime() === today.getTime();
      let hasAvailableSlots = false;

      // Generate time slots for this date and check if any are available
      outerLoop: for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 60) {
          const slotDateTime = new Date(checkDate);
          slotDateTime.setHours(hour, minute, 0, 0);

          // Skip past slots for today
          if (isToday && slotDateTime.getTime() < now.getTime()) {
            continue;
          }

          // Check if slot is within availability window
          const slotTotalMinutes = hour * 60 + minute;
          const isInWindow = dayAvailability.some(avail => {
            if (!avail || !avail.startTime || !avail.endTime) return false;
            const [startHours, startMinutes] = avail.startTime.split(":").map(Number);
            const [endHours, endMinutes] = avail.endTime.split(":").map(Number);
            const startTotalMinutes = startHours * 60 + startMinutes;
            const endTotalMinutes = endHours * 60 + endMinutes;
            return slotTotalMinutes >= startTotalMinutes && slotTotalMinutes < endTotalMinutes;
          });

          if (!isInWindow) continue;

          // Check if slot point itself conflicts with bookings
          const totalInstances =
            space?.totalInstances ||
            space?.numberOfInstances ||
            instances?.length ||
            availabilityInstanceIds?.length ||
            1;

          const slotTime = slotDateTime.getTime();
          const bufferMs = bufferTimeInMinutes * 60 * 1000;

          const busyInstanceIds = new Set();
          filteredBookings.forEach(booking => {
            if (!booking.startTime || !booking.endTime) return;
            const bookingStart = new Date(booking.startTime).getTime();
            const bookingEnd = new Date(booking.endTime).getTime();
            const bookingEndWithBuffer = bookingEnd + bufferMs;

            if (slotTime < bookingEndWithBuffer && slotTime >= bookingStart) {
              const identifier = booking.spaceInstanceId || booking.id || Math.random();
              busyInstanceIds.add(identifier);
            }
          });

          if (busyInstanceIds.size >= totalInstances) continue;

          // Check if this slot + minimum duration conflicts with existing bookings
          // Use same logic as UI rendering to ensure consistency
          const minEndDateTime = new Date(slotDateTime);
          minEndDateTime.setMinutes(minEndDateTime.getMinutes() + minimumDuration * 60);

          const hasRangeConflict = checkTimeRangeConflict(slotDateTime, minEndDateTime);

          // If we found at least one available slot, mark this date as available
          if (!hasRangeConflict) {
            hasAvailableSlots = true;
            break outerLoop; // No need to check more slots
          }
        }
      }

      if (hasAvailableSlots) {
        availableDates.add(checkDate.toDateString());
      }
    }

    return availableDates;
  }, [space, filteredBookings, bufferTimeInMinutes, minimumDuration, dateRefreshTrigger]);

  const availableDatesSet = useMemo(() => getAvailableDates(), [getAvailableDates]);

  const calendarModifiers = useMemo(() => {
    // For HOURLY bookings: don't mark dates as "booked" if they have any booking
    // Only mark as available if there are available slots remaining
    if (durationType === "HOURLY") {
      return {
        available: date => {
          const hasNoBookings = getBookingsForDate(date).length === 0;
          return availableDatesSet.has(date.toDateString()) && hasNoBookings;
        },
        partial: date => {
          const hasBookings = getBookingsForDate(date).length > 0;
          return availableDatesSet.has(date.toDateString()) && hasBookings && !bookedDatesSet.has(date.toDateString());
        },
        booked: date => bookedDatesSet.has(date.toDateString()),
      };
    }

    // For DAILY/WEEKLY/MONTHLY: keep original logic
    // A single booking blocks the entire day
    const modifiers = {
      booked: date => bookedDatesSet.has(date.toDateString()),
      partial: date => {
        const dateStr = date.toDateString();
        const hasBookings = getBookingsForDate(date).length > 0;
        return hasBookings && !bookedDatesSet.has(dateStr);
      },
      available: date => {
        const dateStr = date.toDateString();
        const hasNoBookings = getBookingsForDate(date).length === 0;
        return availableDatesSet.has(dateStr) && hasNoBookings;
      },
    };

    return modifiers;
  }, [bookedDatesSet, availableDatesSet, durationType, getBookingsForDate]);

  // Handle HOURLY time selection
  useEffect(() => {
    if (durationType !== "HOURLY") return;

    if (selectedStartTime && selectedEndTime) {
      // Get all time options to find the selected slots
      const allTimeOptions = generateAllTimeSlots();
      const startSlot = allTimeOptions.find(slot => slot.time === selectedStartTime);
      const endSlot = allTimeOptions.find(slot => slot.time === selectedEndTime);

      if (startSlot && endSlot && onSelectionChangeRef.current) {
        const selectedStart = startSlot.dateTime;
        const selectedEnd = endSlot.dateTime;

        // Check for conflicts with existing bookings for the entire time range
        const hasConflict = checkTimeRangeConflict(selectedStart, selectedEnd);

        onSelectionChangeRef.current({
          startTime: selectedStart,
          endTime: selectedEnd,
          startDate: null,
          endDate: null,
          isValid: !hasConflict,
        });
      }
    } else if (onSelectionChangeRef.current) {
      // If either start or end time is missing, mark as invalid
      onSelectionChangeRef.current({
        startTime: null,
        endTime: null,
        startDate: null,
        endDate: null,
        isValid: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartTime, selectedEndTime, durationType, generateAllTimeSlots, checkTimeRangeConflict]);

  // Handle date range selection (DAILY, WEEKLY, MONTHLY)
  useEffect(() => {
    if (durationType === "HOURLY" || durationType === "MONTHLY") return;

    if (dateRange.from && dateRange.to) {
      // Validate duration constraints
      const start = new Date(dateRange.from);
      const end = new Date(dateRange.to);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days

      // Check duration constraints based on type
      let minDays, maxDays;
      if (durationType === "DAILY") {
        minDays = minimumDuration;
        maxDays = maximumDuration;
      } else if (durationType === "WEEKLY") {
        minDays = minimumDuration * 7;
        maxDays = maximumDuration * 7;
      } else if (durationType === "MONTHLY") {
        minDays = minimumDuration * 30; // Approximate
        maxDays = maximumDuration * 30;
      } else {
        minDays = minimumDuration;
        maxDays = maximumDuration;
      }

      logger.log("[BookingTypeSelector] Duration Logic:", {
        durationType,
        diffDays,
        minDays,
        maxDays,
        minimumDuration,
        maximumDuration,
        dateRangeFrom: dateRange.from,
        dateRangeTo: dateRange.to,
      });

      if (diffDays < minDays || diffDays > maxDays) {
        logger.warn("[BookingTypeSelector] Duration constraint violation", { diffDays, minDays, maxDays });
        return;
      }

      // Check for conflicts
      const conflicts = checkRangeConflicts(start, end);
      if (conflicts && conflicts.length > 0) {
        // Has conflicts - still notify but mark as invalid
        if (onSelectionChangeRef.current) {
          onSelectionChangeRef.current({
            startTime: null,
            endTime: null,
            startDate: start,
            endDate: end,
            conflicts: conflicts,
            isValid: false,
          });
        }
        return;
      }

      // Set start and end times based on duration type
      let finalStart = new Date(start);
      let finalEnd = new Date(end);

      // For DAILY, set times - use exact times if specified, otherwise start/end of day
      if (durationType === "DAILY") {
        // If user has manually set times, preserve them
        if (hasManualTimes && startDateTime && endDateTime) {
          // Preserve user's manually selected times
          finalStart.setHours(startDateTime.getHours(), startDateTime.getMinutes(), 0, 0);
          finalEnd.setHours(endDateTime.getHours(), endDateTime.getMinutes(), 0, 0);
        } else if (startDateTime && endDateTime) {
          // Use existing times (from category defaults or previous selections)
          finalStart.setHours(startDateTime.getHours(), startDateTime.getMinutes(), 0, 0);
          finalEnd.setHours(endDateTime.getHours(), endDateTime.getMinutes(), 0, 0);
        } else {
          // No times set - use category defaults
          const categoryName = space?.category?.name || space?.categoryName || "WORK";
          const defaultRange = AvailabilityUtils.getDefaultTimeRange(categoryName, "DAILY", finalStart);
          finalStart.setHours(defaultRange.start.getHours(), defaultRange.start.getMinutes(), 0, 0);
          finalEnd.setHours(defaultRange.end.getHours(), defaultRange.end.getMinutes(), 0, 0);
          // Set the state so UI shows these defaults
          // NOTE: This will re-trigger this effect, but the next run
          // will take the 'startDateTime && endDateTime' branch above, breaking the cycle.
          setStartDateTime(new Date(finalStart));
          setEndDateTime(new Date(finalEnd));
          // Don't call onSelectionChange here - let the re-triggered effect handle it
          return;
        }
      } else {
        // For WEEKLY/MONTHLY, use start and end of day
        finalStart.setHours(0, 0, 0, 0);
        finalEnd.setHours(23, 59, 59, 999);
      }

      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current({
          startTime: finalStart,
          endTime: finalEnd,
          startDate: finalStart,
          endDate: finalEnd,
          isValid: true,
        });
      }
    }
  }, [
    dateRange,
    durationType,
    minimumDuration,
    maximumDuration,
    startDateTime,
    endDateTime,
    hasManualTimes,
    space,
    checkRangeConflicts,
    // NOTE: onSelectionChange intentionally omitted from deps to prevent infinite loops.
    // We use onSelectionChangeRef instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const monthlyEndDate = useMemo(() => {
    if (!monthlyStartDate) return null;
    const totalDays = (monthlyMonthCount || 1) * 30;
    return addDays(monthlyStartDate, Math.max(totalDays - 1, 0));
  }, [monthlyStartDate, monthlyMonthCount]);

  useEffect(() => {
    if (durationType !== "MONTHLY") return;

    if (!monthlyStartDate || !monthlyEndDate) {
      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current({
          startTime: null,
          endTime: null,
          startDate: null,
          endDate: null,
          isValid: false,
        });
      }
      return;
    }

    const start = new Date(monthlyStartDate);
    const end = new Date(monthlyEndDate);

    // Apply category-specific times for MONTHLY bookings
    const categoryName = space?.category?.name || space?.categoryName || "WORK";
    const monthlyRange = AvailabilityUtils.getDefaultTimeRange(categoryName, "MONTHLY", start);
    start.setHours(monthlyRange.start.getHours(), monthlyRange.start.getMinutes(), 0, 0);
    end.setHours(monthlyRange.end.getHours(), monthlyRange.end.getMinutes(), 0, 0);

    const conflicts = checkRangeConflicts(start, end);
    if (conflicts && conflicts.length > 0) {
      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current({
          startTime: start,
          endTime: end,
          startDate: start,
          endDate: end,
          conflicts,
          isValid: false,
        });
      }
      return;
    }

    if (onSelectionChangeRef.current) {
      onSelectionChangeRef.current({
        startTime: start,
        endTime: end,
        startDate: start,
        endDate: end,
        isValid: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyStartDate, monthlyEndDate, durationType, checkRangeConflicts]);

  const renderConflictAlert = (conflicts, description) => (
    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
      <p className="text-sm text-red-800 font-medium mb-1">⚠️ Booking conflicts detected</p>
      <p className="text-xs text-red-700">{description}</p>
      <div className="mt-2 space-y-1">
        {conflicts.slice(0, 3).map((conflict, idx) => (
          <p key={idx} className="text-xs text-red-600">
            • {formatDate(conflict.date, "MMM dd, yyyy")}: {conflict.bookings.length} booking(s)
          </p>
        ))}
        {conflicts.length > 3 && <p className="text-xs text-red-600">... and {conflicts.length - 3} more</p>}
      </div>
    </div>
  );

  const monthlyConflictWarning = useMemo(() => {
    if (durationType !== "MONTHLY" || !monthlyStartDate || !monthlyEndDate) return null;
    const startDate = new Date(monthlyStartDate);
    const endDate = new Date(monthlyEndDate);
    const conflicts = checkRangeConflicts(startDate, endDate);
    if (conflicts && conflicts.length > 0) {
      return renderConflictAlert(
        conflicts,
        "There are existing bookings within the selected timeframe. Please choose a different start date or duration."
      );
    }
    return null;
  }, [durationType, monthlyStartDate, monthlyEndDate, checkRangeConflicts]);

  const rangeConflictWarning = useMemo(() => {
    if (durationType === "MONTHLY" || !dateRange.from || !dateRange.to) return null;
    const conflicts = checkRangeConflicts(dateRange.from, dateRange.to);
    if (conflicts && conflicts.length > 0) {
      return renderConflictAlert(
        conflicts,
        "There are existing bookings within the selected date range. Please choose a different range."
      );
    }
    return null;
  }, [durationType, dateRange.from, dateRange.to, checkRangeConflicts]);

  // Handle state reset and default initialization when duration type or space changes
  useEffect(() => {
    if (initialSelection?.window) {
      return;
    }

    // Reset all selection states
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setDateRange({ from: null, to: null });
    setStartDateTime(null);
    setEndDateTime(null);
    setDatePickerValue(null);
    setMonthlyStartDate(null);
    setHasManualTimes(false);

    if (!space) return;

    const categoryName = space.category?.name || space.categoryName || "WORK";

    if (durationType === "DAILY") {
      const today = new Date();
      setDateRange({ from: today, to: today });

      const { start, end } = AvailabilityUtils.getDefaultTimeRange(categoryName, "DAILY", today);

      const safeStartHour = calculateSafeStartTime(today, categoryName);
      const finalStart = new Date(start);
      if (today.toDateString() === start.toDateString() && safeStartHour > start.getHours()) {
        finalStart.setHours(safeStartHour, 0, 0, 0);
      }

      setStartDateTime(finalStart);
      setEndDateTime(end);
      setDatePickerValue({ startDate: today, endDate: today });

      const conflicts = checkRangeConflicts(finalStart, end);

      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current({
          startTime: finalStart,
          endTime: end,
          duration: Math.ceil((end - finalStart) / (1000 * 60)),
          durationLabel: "Daily",
          isValid: !conflicts || conflicts.length === 0,
          conflicts,
        });
      }
    } else if (durationType === "MONTHLY") {
      // Monthly default logic
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const safeStartHour = calculateSafeStartTime(today, categoryName);

      const defaultStart = new Date(today);
      defaultStart.setHours(safeStartHour, 0, 0, 0);
      const defaultEnd = new Date(nextMonth);
      defaultEnd.setHours(23, 59, 0, 0);

      setStartDateTime(defaultStart);
      setEndDateTime(defaultEnd);
      setDatePickerValue({ startDate: today, endDate: nextMonth });

      const conflicts = checkRangeConflicts(defaultStart, defaultEnd);

      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current({
          startTime: defaultStart,
          endTime: defaultEnd,
          duration: 30 * 24 * 60,
          durationLabel: "1 month",
          isValid: !conflicts || conflicts.length === 0,
          conflicts,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space, durationType, calculateSafeStartTime]);

  useEffect(() => {
    const minMonths = Math.max(1, minimumDuration);
    const maxMonths = Math.max(minMonths, maximumDuration);
    setMonthlyMonthCount(prev => {
      if (!prev) return minMonths;
      return Math.min(Math.max(prev, minMonths), maxMonths);
    });
  }, [minimumDuration, maximumDuration]);
  const handleSmartDatePickerChange = useCallback(
    value => {
      setDatePickerValue(value);

      if (!value) {
        setDateRange({ from: null, to: null });
        setStartDateTime(null);
        setEndDateTime(null);
        return;
      }

      let newDateRange = { from: null, to: null };

      if (value.startDate && value.endDate) {
        newDateRange = {
          from: new Date(value.startDate),
          to: new Date(value.endDate),
        };
      } else if (Array.isArray(value.specificDate) && value.specificDate.length > 0) {
        const sorted = [...value.specificDate].sort();
        newDateRange = {
          from: new Date(sorted[0]),
          to: new Date(sorted[sorted.length - 1]),
        };
      }

      setDateRange(newDateRange);

      // Preserve or update times based on the new date range
      if (newDateRange.from && newDateRange.to) {
        // space is already destructured from props
        const categoryName = space?.category?.name || space?.categoryName || "WORK";

        if (durationType === "DAILY") {
          const { start, end } = AvailabilityUtils.getDefaultTimeRange(categoryName, "DAILY", newDateRange.from);

          // Use safe start time for today
          const today = new Date();
          let finalStart = new Date(start);
          if (newDateRange.from.toDateString() === today.toDateString()) {
            const safeStartHour = calculateSafeStartTime(today, categoryName);
            if (safeStartHour > start.getHours()) {
              finalStart.setHours(safeStartHour, 0, 0, 0);
            }
          }

          // Adjust end date if range is multiple days
          const finalEnd = new Date(end);
          if (newDateRange.to.getTime() > newDateRange.from.getTime()) {
            finalEnd.setFullYear(newDateRange.to.getFullYear());
            finalEnd.setMonth(newDateRange.to.getMonth());
            finalEnd.setDate(newDateRange.to.getDate());
          }

          setStartDateTime(finalStart);
          setEndDateTime(finalEnd);

          if (onSelectionChange) {
            logger.log("Updating DAILY selection", { finalStart, finalEnd });
            onSelectionChange({
              startTime: finalStart,
              endTime: finalEnd,
              duration: Math.ceil((finalEnd - finalStart) / (1000 * 60)),
              durationLabel: "Daily",
              isValid: true,
            });
          }
        } else if (durationType === "WEEKLY") {
          // Add support for weekly immediate update
          const finalStart = new Date(newDateRange.from);
          const finalEnd = new Date(newDateRange.to);
          finalStart.setHours(0, 0, 0, 0);
          finalEnd.setHours(23, 59, 59, 999);

          logger.log("Updating WEEKLY selection", { finalStart, finalEnd });
          if (onSelectionChange) {
            onSelectionChange({
              startTime: finalStart,
              endTime: finalEnd,
              startDate: finalStart,
              endDate: finalEnd,
              isValid: true, // Initial validity, useEffect will refine
            });
          }
        }
      } else {
        // No valid date range, clear times
        setStartDateTime(null);
        setEndDateTime(null);
      }
    },
    [durationType, space, startDateTime, endDateTime]
  );

  const getTimeframeDetails = useCallback(() => {
    switch ((durationType || "").toUpperCase()) {
      case "HOURLY":
        return {
          title: "Hourly timeframe",
          selection: "Pick a day, then choose start and end times within that day.",
          components: "Year • Month • Hours in a day",
        };
      case "DAILY":
        return {
          title: "Daily timeframe",
          selection: "Select a single day or a contiguous day range.",
          components: "Year • Month",
        };
      case "WEEKLY":
        return {
          title: "Weekly timeframe",
          selection: "Pick week-long windows (minimum 7 days).",
          components: "Year • Month • Week guidance",
        };
      case "MONTHLY":
        return {
          title: "Monthly timeframe",
          selection: "Tap months to reserve 30-day blocks.",
          components: "Year-focused month view",
        };
      default:
        return null;
    }
  }, [durationType]);

  const timeframeHint = getTimeframeDetails();

  const getInstancePreviewStatuses = useCallback(
    date => {
      if (!date) return null;

      // Use instances prop if available, otherwise fall back to availabilityInstanceIds
      let effectiveInstances = instances;
      if (!Array.isArray(instances) || instances.length === 0) {
        if (Array.isArray(availabilityInstanceIds) && availabilityInstanceIds.length > 0) {
          // Create minimal instance objects from availability endpoint IDs
          effectiveInstances = availabilityInstanceIds.map((id, index) => ({
            id,
            title: `Unit #${index + 1}`,
          }));
        } else {
          return null;
        }
      }

      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      const overlapMs = (startA, endA, startB, endB) => Math.max(0, Math.min(endA, endB) - Math.max(startA, startB));

      return effectiveInstances.map(inst => {
        const bookings = (existingBookings || []).filter(b => {
          const bInst = b?.spaceInstanceId ?? b?.spaceInstance?.id ?? null;
          if (bInst && inst.id && bInst !== inst.id) return false;
          const start = new Date(b.startTime);
          const end = new Date(b.endTime);
          if (Number.isNaN(start?.getTime()) || Number.isNaN(end?.getTime())) return false;
          return start < dayEnd && end > dayStart;
        });

        const totalBookedMs = bookings.reduce((acc, b) => {
          const start = new Date(b.startTime);
          const end = new Date(b.endTime);
          return acc + overlapMs(start.getTime(), end.getTime(), dayStart.getTime(), dayEnd.getTime());
        }, 0);

        const slots = (availableTimeSlots || []).filter(slot => {
          const slotInst = slot?.spaceInstanceId ?? slot?.instanceId ?? null;
          if (slotInst && inst.id && slotInst !== inst.id) return false;
          const start = new Date(slot.startTime || slot.dateTime || slot.rawStartTime);
          const end = new Date(slot.endTime || slot.rawEndTime || slot.endDateTime || slot.startTime);
          if (Number.isNaN(start?.getTime()) || Number.isNaN(end?.getTime())) return false;
          return start < dayEnd && end > dayStart;
        });

        const totalAvailMs = slots.reduce((acc, slot) => {
          const start = new Date(slot.startTime || slot.dateTime || slot.rawStartTime);
          const end = new Date(slot.endTime || slot.rawEndTime || slot.endDateTime || slot.startTime);
          return acc + overlapMs(start.getTime(), end.getTime(), dayStart.getTime(), dayEnd.getTime());
        }, 0);

        const categoryName = space?.category?.name || space?.categoryName || "WORK";
        let effectiveAvail = totalAvailMs > 0 ? totalAvailMs : 24 * 60 * 60 * 1000;

        // Ensure STAY spaces always consider 24h as the baseline for partial/full calculation
        if (categoryName.toUpperCase() === "STAY") {
          effectiveAvail = 24 * 60 * 60 * 1000;
        }

        let label = "Available";
        let color = "bg-green-500";
        if (totalBookedMs >= effectiveAvail - 60000) {
          label = "Fully booked";
          color = "bg-red-500";
        } else if (totalBookedMs > 0) {
          label = "Partial";
          color = "bg-orange-400"; // Enforce orange for partial
        }

        return {
          id: inst.id,
          title: inst.title || (inst.instanceNumber ? `Unit #${inst.instanceNumber}` : `Unit #${inst.id}`),
          status: { label, color },
        };
      });
    },
    [availableTimeSlots, availabilityInstanceIds, existingBookings, instances, space]
  );

  const getDayStatus = useCallback(
    date => {
      if (!date) return null;
      const statuses = getInstancePreviewStatuses(date) || [];
      if (statuses.length > 0) {
        const allFull = statuses.every(s => s.status?.label === "Fully booked");
        if (allFull) return "full";
        const anyBooked = statuses.some(s => s.status?.label && s.status.label !== "Available");
        if (anyBooked) return "partial";
        return null;
      }
      const bookings = getBookingsForDate(date);
      if (bookings.length > 0) {
        // Fallback for when instance data is missing but we have bookings
        const totalInstances = space?.totalInstances || space?.numberOfInstances || instances?.length || 1;

        // Count busy instances
        const busyIds = new Set();
        bookings.forEach(b => {
          if (b.spaceInstanceId) busyIds.add(b.spaceInstanceId);
          else busyIds.add(`legacy_${b.id}`);
        });

        if (busyIds.size >= totalInstances) return "full";
        return "partial";
      }
      return null;
    },
    [getInstancePreviewStatuses, getBookingsForDate]
  );

  // Scroll button components for instance list
  const InstanceScrollUpButton = ({ viewportRef }) => {
    const scrollIntervalRef = React.useRef(null);

    const startScrolling = () => {
      if (scrollIntervalRef.current) return;
      scrollIntervalRef.current = setInterval(() => {
        if (viewportRef.current) {
          viewportRef.current.scrollTop -= 10;
        }
      }, 50);
    };

    const stopScrolling = () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };

    return (
      <div
        className="flex cursor-pointer items-center justify-center py-1 hover:bg-gray-100 rounded"
        onMouseDown={startScrolling}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
      >
        <ChevronUp className="h-4 w-4 text-gray-500" />
      </div>
    );
  };

  const InstanceScrollDownButton = ({ viewportRef }) => {
    const scrollIntervalRef = React.useRef(null);

    const startScrolling = () => {
      if (scrollIntervalRef.current) return;
      scrollIntervalRef.current = setInterval(() => {
        if (viewportRef.current) {
          viewportRef.current.scrollTop += 10;
        }
      }, 50);
    };

    const stopScrolling = () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };

    return (
      <div
        className="flex cursor-pointer items-center justify-center py-1 hover:bg-gray-100 rounded"
        onMouseDown={startScrolling}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
      >
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </div>
    );
  };

  // Scrollable instance list component
  const ScrollableInstanceList = ({ statuses, maxVisible = 5 }) => {
    const viewportRef = React.useRef(null);
    const needsScrolling = statuses.length > maxVisible;
    const listHeight = Math.min(statuses.length, maxVisible) * 28; // 28px per item approx

    return (
      <div className="relative">
        {needsScrolling && <InstanceScrollUpButton viewportRef={viewportRef} />}
        <div
          ref={viewportRef}
          className="space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          style={{ maxHeight: needsScrolling ? `${listHeight}px` : "none" }}
        >
          {statuses.map(item => (
            <div key={item.id} className="flex items-center justify-between text-xs py-0.5">
              <span className="text-gray-800">{item.title}</span>
              <div className="flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${item.status?.color || "bg-gray-400"}`} />
                <span className="text-gray-700">{item.status?.label || "Unknown"}</span>
              </div>
            </div>
          ))}
        </div>
        {needsScrolling && <InstanceScrollDownButton viewportRef={viewportRef} />}
        {needsScrolling && (
          <p className="text-[10px] text-gray-400 mt-1 text-center">{statuses.length} units • Use arrows to scroll</p>
        )}
      </div>
    );
  };

  // Scrollable instance list for calendar day tooltips (handles instanceStatuses format)
  const ScrollableInstanceStatusList = ({ instanceStatuses, maxVisible = 5 }) => {
    const viewportRef = React.useRef(null);
    const needsScrolling = instanceStatuses.length > maxVisible;
    const listHeight = Math.min(instanceStatuses.length, maxVisible) * 28; // 28px per item approx

    return (
      <div className="relative">
        {needsScrolling && <InstanceScrollUpButton viewportRef={viewportRef} />}
        <div
          ref={viewportRef}
          className="space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          style={{ maxHeight: needsScrolling ? `${listHeight}px` : "none" }}
        >
          {instanceStatuses.map(({ instance, status }) => (
            <div key={instance.id} className="flex items-center justify-between text-xs py-0.5">
              <span className="text-gray-800">
                {instance.title ||
                  (instance.instanceNumber ? `Unit #${instance.instanceNumber}` : `Unit #${instance.id}`)}
              </span>
              <div className="flex items-center gap-1">
                <span className={`h-2 w-2 rounded-full ${status?.color || "bg-gray-400"}`} />
                <span className="text-gray-700">{status?.label || "Unknown"}</span>
              </div>
            </div>
          ))}
        </div>
        {needsScrolling && <InstanceScrollDownButton viewportRef={viewportRef} />}
        {needsScrolling && (
          <p className="text-[10px] text-gray-400 mt-1 text-center">
            {instanceStatuses.length} units • Use arrows to scroll
          </p>
        )}
      </div>
    );
  };

  const renderInstancePreview = previewDate => {
    const statuses = getInstancePreviewStatuses(previewDate) || [];
    return (
      <Popover open={isInstancePreviewOpen} onOpenChange={setIsInstancePreviewOpen}>
        <div
          onMouseEnter={() => setIsInstancePreviewOpen(true)}
          onMouseLeave={() => setIsInstancePreviewOpen(false)}
          className="relative"
        >
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="h-10 w-10" disabled={!previewDate}>
              <Building2 className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72" side="top" align="end">
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-medium">All instances for selected date</p>
              {!previewDate && <p className="text-xs text-gray-500">Select a date to see availability.</p>}
              {previewDate && statuses.length === 0 && <p className="text-xs text-gray-500">No instance data.</p>}
              {previewDate && statuses.length > 0 && <ScrollableInstanceList statuses={statuses} />}
            </div>
          </PopoverContent>
        </div>
      </Popover>
    );
  };

  const getPreviewDateForSmartPicker = value => {
    if (!value) return null;
    if (value.startDate) return new Date(value.startDate);
    if (Array.isArray(value.specificDate) && value.specificDate.length > 0) return new Date(value.specificDate[0]);
    return null;
  };

  // Helper to format duration unit
  const getDurationUnit = () => {
    switch (durationType) {
      case "HOURLY":
        return minimumDuration === 1 ? "hour" : "hours";
      case "DAILY":
        return minimumDuration === 1 ? "day" : "days";
      case "WEEKLY":
        return minimumDuration === 1 ? "week" : "weeks";
      case "MONTHLY":
        return minimumDuration === 1 ? "month" : "months";
      default:
        return "hour";
    }
  };

  // HOURLY booking selector
  if (durationType === "HOURLY") {
    const startTimeOptions = getAllTimeOptions();
    const endTimeOptions = getEndTimeOptions();
    const durationUnit = getDurationUnit();

    return (
      <div className="space-y-6">
        {timeframeHint && (
          <div className="p-3 rounded-lg border border-indigo-100 bg-indigo-50">
            <p className="text-sm font-semibold text-indigo-900">{timeframeHint.title}</p>
            {/* <p className="text-xs text-indigo-700 mt-1">{timeframeHint.selection}</p>
            <p className="text-xs text-indigo-600 mt-1">
              Components: <span className="font-medium">{timeframeHint.components}</span>
            </p> */}
          </div>
        )}

        {/* Date Selection for HOURLY */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="block text-sm font-semibold text-gray-900">
              <Calendar className="w-4 h-4 inline mr-2 text-gray-600" />
              Select Date
            </Label>
          </div>
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <div className="flex items-center gap-2">
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "flex-1 justify-start text-left font-normal h-10",
                    !dateRange.from && "text-muted-foreground"
                  )}
                  disabled={disabled}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {dateRange.from ? formatDate(dateRange.from, "LLL dd, y") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              {instances?.length > 0 &&
                renderInstancePreview(
                  durationType === "HOURLY" ? dateRange?.from || null : dateRange?.from || selectedDate || null
                )}
            </div>
            <PopoverContent className="w-auto p-0" align="start">
              <TooltipProvider>
                <ShadcnCalendar
                  mode="range"
                  selected={dateRange}
                  onSelect={range => {
                    if (range?.from) {
                      const newRange = { from: range.from, to: range.to || range.from };
                      setDateRange(newRange);
                      setIsCalendarOpen(false);
                      // Clear time selections when date changes
                      setSelectedStartTime(null);
                      setSelectedEndTime(null);

                      // Notify parent component of date change
                      if (onDateChange) {
                        onDateChange(range.from);
                      }
                    }
                  }}
                  disabled={date => {
                    if (!date) return true;
                    if (date < startOfToday) return true;
                    if (date > maxSelectableDate) return true;
                    return hasAvailabilityConstraints && !isDateWithinAvailability(date);
                  }}
                  modifiers={calendarModifiers}
                  modifiersStyles={calendarModifiersStyles}
                  components={{
                    DayContent: ({ date }) => {
                      const bookings = getBookingsForDate(date);
                      const totalInstances =
                        space?.totalInstances || space?.numberOfInstances || instances?.length || 1;
                      const isBooked = bookings.length >= totalInstances;
                      const hasInstanceStatus = instanceDayStatusFn && Array.isArray(instances) && instances.length > 0;
                      const instanceStatuses = hasInstanceStatus
                        ? instances.map(inst => ({
                            instance: inst,
                            status: instanceDayStatusFn(inst, date),
                          }))
                        : [];

                      const dayStatus = getDayStatus(date); // full | partial | null

                      const aggregateStatus = (() => {
                        if (!hasInstanceStatus) return { label: null, color: null };

                        const allFull = instanceStatuses.every(({ status }) => status?.label === "Fully booked");
                        if (allFull) return { label: "Fully booked", color: "bg-red-500", score: 3 };

                        const anyBooked = instanceStatuses.some(
                          ({ status }) => status?.label && status.label !== "Available"
                        );
                        if (anyBooked) return { label: "Partial", color: "bg-orange-400", score: 2 };

                        return { label: "Available", color: "bg-green-500", score: 1 };
                      })();

                      const dayNumber = (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                          <span
                            className={cn(
                              dayStatus === "full" && "text-red-600 font-semibold",
                              dayStatus === "partial" && "text-orange-600 font-semibold"
                            )}
                          >
                            {date.getDate()}
                          </span>
                          {aggregateStatus?.color && (
                            <span className={`h-1.5 w-1.5 rounded-full ${aggregateStatus.color}`} />
                          )}
                          {!aggregateStatus?.color && dayStatus && (
                            <span
                              className={cn(
                                "h-1.5 w-1.5 rounded-full",
                                dayStatus === "full" ? "bg-red-500" : "bg-orange-400"
                              )}
                            />
                          )}
                        </div>
                      );

                      // Always show tooltip with instance info
                      return (
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>{dayNumber}</TooltipTrigger>
                            <TooltipContent className="w-72 z-[9999]" side="top">
                              <div className="space-y-2">
                                {isBooked && (
                                  <div className="text-sm">
                                    <p className="font-medium">
                                      {bookings.length} booking{bookings.length > 1 ? "s" : ""}
                                    </p>
                                    {bookings.slice(0, 2).map((booking, idx) => (
                                      <p key={idx} className="text-xs">
                                        {formatDate(new Date(booking.startTime), "HH:mm")} -{" "}
                                        {formatDate(new Date(booking.endTime), "HH:mm")}
                                      </p>
                                    ))}
                                    {bookings.length > 2 && (
                                      <p className="text-xs">... and {bookings.length - 2} more</p>
                                    )}
                                  </div>
                                )}

                                {hasInstanceStatus && (
                                  <div className="space-y-1">
                                    <p className="text-xs text-gray-500 font-medium">Availability</p>
                                    <ScrollableInstanceStatusList instanceStatuses={instanceStatuses} />
                                  </div>
                                )}

                                {!isBooked && !hasInstanceStatus && (
                                  <p className="text-xs text-gray-500">No availability info</p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    },
                  }}
                  initialFocus
                />
              </TooltipProvider>
            </PopoverContent>
          </Popover>
          <p className="text-xs text-gray-500 mt-2">
            {instances?.length > 0 && (
              <span className="text-blue-600">Hover the building icon to see all instances.</span>
            )}
          </p>
        </div>

        {/* Booking Duration Info */}
        {/* <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Booking Duration</p>
              <p className="text-xs text-blue-700">
                Minimum:{" "}
                <span className="font-semibold">
                  {minimumDuration} {durationUnit}
                </span>{" "}
                • Maximum:{" "}
                <span className="font-semibold">
                  {maximumDuration} {durationUnit}
                </span>
              </p>
            </div>
          </div>
        </div> */}

        {/* Time Slots - Only show when date is selected */}
        {dateRange.from && (
          <>
            {/* Start Time */}
            {/* Start Time */}
            <div>
              <Label className="block text-sm font-semibold text-gray-900 mb-3">
                <Clock className="w-4 h-4 inline mr-2 text-gray-600" />
                Start Time
              </Label>

              <div ref={startTimeContainerRef} className="overflow-x-auto pb-2 -mx-1 px-1">
                <div className="flex gap-2 min-w-max">
                  {startTimeOptions.map((option, index) => {
                    // Check for booking conflicts - use same logic as final validation
                    // Check if selecting this start time with minimum duration would conflict
                    const hasBookingConflict = (() => {
                      if (!effectiveSelectedDate || filteredBookings.length === 0) return false;

                      // Use the option's dateTime directly (already includes the correct date)
                      const slotStartDateTime = option.dateTime;

                      // Calculate minimum end time (start + minimum duration)
                      const minEndDateTime = new Date(slotStartDateTime);
                      minEndDateTime.setMinutes(minEndDateTime.getMinutes() + minimumDuration * 60);

                      // Check if this range (start to min end) would conflict with any booking
                      return checkTimeRangeConflict(slotStartDateTime, minEndDateTime);
                    })();

                    const isDisabled = disabled || !option.available || hasBookingConflict;
                    const isSelected = selectedStartTime === option.time;

                    const availabilityInfo = option.availabilityDetails || {};
                    const isPartial = availabilityInfo.bookedCount > 0 && option.available;

                    return (
                      <TooltipProvider key={index}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => {
                                if (option.available && !disabled && !hasBookingConflict) {
                                  setSelectedStartTime(option.time);
                                  setSelectedEndTime(null);
                                }
                              }}
                              disabled={isDisabled}
                              className={cn(
                                "px-4 py-2.5 text-sm font-medium rounded-lg border-2 transition-all whitespace-nowrap flex-shrink-0 relative",
                                isSelected
                                  ? "bg-blue-600 border-blue-600 text-white shadow-lg"
                                  : isDisabled
                                    ? hasBookingConflict
                                      ? "bg-red-100 border-red-300 text-red-600 cursor-not-allowed opacity-75"
                                      : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-50"
                                    : isPartial
                                      ? "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 hover:border-orange-300"
                                      : "bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300"
                              )}
                            >
                              <div className={cn("font-semibold", isDisabled && "opacity-60")}>{option.label}</div>
                              <div className={cn("text-xs opacity-75 mt-0.5", isDisabled && "opacity-40")}>
                                {option.durationLabel}
                              </div>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="z-50">
                            {hasBookingConflict ? (
                              <div className="text-sm">
                                <p className="font-medium text-red-600">Already booked</p>
                                <p className="text-xs">This time slot conflicts with an existing booking</p>
                              </div>
                            ) : isDisabled ? (
                              <p className="text-sm">Not available</p>
                            ) : isPartial ? (
                              <div className="text-sm">
                                <p className="font-medium text-orange-700">{option.label}</p>
                                <p className="text-xs font-medium">
                                  {availabilityInfo.bookedCount}/{availabilityInfo.totalInstances} units booked
                                </p>
                                <p className="text-[10px] opacity-75">{option.durationLabel}</p>
                              </div>
                            ) : (
                              <div className="text-sm">
                                <p className="font-medium">{option.label}</p>
                                <p className="text-xs">{option.durationLabel}</p>
                              </div>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* End Time */}
            {selectedStartTime && (
              <div>
                <Label className="block text-sm font-semibold text-gray-900 mb-3">
                  <Clock className="w-4 h-4 inline mr-2 text-gray-600" />
                  End Time
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    (min {minimumDuration} {durationUnit})
                  </span>
                </Label>

                {endTimeOptions.length > 0 ? (
                  <div className="overflow-x-auto pb-2 -mx-1 px-1">
                    <div className="flex gap-2 min-w-max">
                      {endTimeOptions.map((option, index) => {
                        // Check for booking conflicts - use same logic as final validation
                        // Check if selected start time + this end time would conflict
                        const hasBookingConflict = (() => {
                          if (!effectiveSelectedDate || !selectedStartTime || filteredBookings.length === 0) return false;

                          // Get the start slot's dateTime (already includes the correct date)
                          const startSlot = getAllTimeOptions().find(slot => slot.time === selectedStartTime);
                          if (!startSlot) return false;

                          const rangeStart = startSlot.dateTime;
                          const rangeEnd = option.dateTime;

                          // Check if this range (selected start to this end) would conflict with any booking
                          return checkTimeRangeConflict(rangeStart, rangeEnd);
                        })();

                        const isDisabled = disabled || !option.available || hasBookingConflict;
                        const isSelected = selectedEndTime === option.time;

                        const availabilityInfo = option.availabilityDetails || {};
                        const isPartial = availabilityInfo.bookedCount > 0 && option.available;

                        return (
                          <TooltipProvider key={index}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (option.available && !disabled && !hasBookingConflict) {
                                      setSelectedEndTime(option.time);
                                    }
                                  }}
                                  disabled={isDisabled}
                                  className={cn(
                                    "px-4 py-2.5 text-sm font-medium rounded-lg border-2 transition-all whitespace-nowrap flex-shrink-0 relative",
                                    isSelected
                                      ? "bg-blue-600 border-blue-600 text-white shadow-lg"
                                      : isDisabled
                                        ? hasBookingConflict
                                          ? "bg-red-100 border-red-300 text-red-600 cursor-not-allowed opacity-75"
                                          : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-50"
                                        : isPartial
                                          ? "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100 hover:border-orange-300"
                                          : "bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300"
                                  )}
                                >
                                  <div className={cn("font-semibold", isDisabled && "opacity-60")}>{option.label}</div>
                                  <div className={cn("text-xs opacity-75 mt-0.5", isDisabled && "opacity-40")}>
                                    {option.durationLabel}
                                  </div>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="z-50">
                                {hasBookingConflict ? (
                                  <div className="text-sm">
                                    <p className="font-medium text-red-600">Already booked</p>
                                    <p className="text-xs">This time slot conflicts with an existing booking</p>
                                  </div>
                                ) : isDisabled ? (
                                  <p className="text-sm">Not available</p>
                                ) : isPartial ? (
                                  <div className="text-sm">
                                    <p className="font-medium text-orange-700">{option.label}</p>
                                    <p className="text-xs font-medium">
                                      {availabilityInfo.bookedCount}/{availabilityInfo.totalInstances} units booked
                                    </p>
                                    <p className="text-[10px] opacity-75">{option.durationLabel}</p>
                                  </div>
                                ) : (
                                  <div className="text-sm">
                                    <p className="font-medium">{option.label}</p>
                                    <p className="text-xs">{option.durationLabel}</p>
                                  </div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">No valid end times available from this start time.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // DAILY, WEEKLY, MONTHLY booking selector (date range)
  const durationUnit = getDurationUnit();
  const maxDurationUnit = () => {
    switch (durationType) {
      case "DAILY":
        return maximumDuration === 1 ? "day" : "days";
      case "WEEKLY":
        return maximumDuration === 1 ? "week" : "weeks";
      case "MONTHLY":
        return maximumDuration === 1 ? "month" : "months";
      default:
        return "day";
    }
  };

  return (
    <div className="space-y-6">
      {timeframeHint && (
        <div className="p-3 rounded-lg border border-indigo-100 bg-indigo-50">
          <p className="text-sm font-semibold text-indigo-900">{timeframeHint.title}</p>
          {/* <p className="text-xs text-indigo-700 mt-1">{timeframeHint.selection}</p>
          <p className="text-xs text-indigo-600 mt-1">
            Components: <span className="font-medium">{timeframeHint.components}</span>
          </p> */}
        </div>
      )}
      {/* Booking Duration Info (hidden intentionally) */}

      {/* Monthly flow uses start date + auto end */}
      {durationType === "MONTHLY" ? (
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-semibold text-gray-900">
              Choose a start date
              <span className="ml-1 text-xs font-normal text-gray-500">
                (we'll add {monthlyMonthCount * 30} days automatically)
              </span>
            </Label>
            <Popover open={isMonthlyCalendarOpen} onOpenChange={setIsMonthlyCalendarOpen}>
              <div className="flex items-center gap-2 mt-2">
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={disabled}
                    className={cn(
                      "flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm text-left flex items-center justify-between",
                      disabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {monthlyStartDate ? formatDate(monthlyStartDate, "MMM dd, yyyy") : "Pick a start date"}
                    </span>
                    <Calendar className="w-4 h-4 text-gray-500" />
                  </button>
                </PopoverTrigger>
                {instances?.length > 0 && renderInstancePreview(monthlyStartDate)}
              </div>
              <PopoverContent className="w-auto p-2" align="start">
                <TooltipProvider>
                  <ShadcnCalendar
                    mode="single"
                    selected={monthlyStartDate}
                    onSelect={date => {
                      if (!date) return;
                      setMonthlyStartDate(date);
                      setIsMonthlyCalendarOpen(false);
                    }}
                    disabled={date => {
                      if (!date) return true;
                      if (date < minSelectableDate) return true; // Respect cutoff logic
                      if (maxSelectableDate && date > maxSelectableDate) return true;
                      if (disabled) return true;
                      return false;
                    }}
                    modifiers={{
                      full: date => {
                        if (instanceDayStatusFn && Array.isArray(instances) && instances.length > 0) {
                          const statuses = instances.map(inst => instanceDayStatusFn(inst, date));
                          return statuses.every(s => s?.label === "Fully booked");
                        }
                        const bookings = getBookingsForDate(date);
                        const totalInstances =
                          space?.totalInstances || space?.numberOfInstances || instances?.length || 1;
                        return bookings.length >= totalInstances;
                      },
                      partial: date => {
                        if (instanceDayStatusFn && Array.isArray(instances) && instances.length > 0) {
                          const statuses = instances.map(inst => instanceDayStatusFn(inst, date));
                          const allFull = statuses.every(s => s?.label === "Fully booked");
                          const anyBooked = statuses.some(s => s?.label && s.label !== "Available");
                          return anyBooked && !allFull;
                        }
                        return false;
                      },
                      past: date => date < minSelectableDate,
                    }}
                    modifiersStyles={{
                      full: { backgroundColor: "#fee2e2", color: "#dc2626", fontWeight: "bold" },
                      partial: { backgroundColor: "#ffedd5", color: "#d97706", fontWeight: "bold" },
                      past: { opacity: 0.6 },
                    }}
                    components={{
                      DayContent: ({ date }) => {
                        const bookings = getBookingsForDate(date);
                        const totalInstances =
                          space?.totalInstances || space?.numberOfInstances || instances?.length || 1;
                        const isBooked = bookings.length >= totalInstances;
                        const hasInstanceStatus =
                          instanceDayStatusFn && Array.isArray(instances) && instances.length > 0;
                        const instanceStatuses = hasInstanceStatus
                          ? instances.map(inst => ({
                              instance: inst,
                              status: instanceDayStatusFn(inst, date),
                            }))
                          : [];

                        const handleHover = () => {
                          try {
                            logger.log("[BookingTypeSelector] hover day", {
                              date: date?.toISOString?.() || date,
                              bookingsCount: bookings.length,
                              instances: instanceStatuses.map(({ instance, status }) => ({
                                id: instance.id,
                                title: instance.title,
                                label: status?.label,
                              })),
                            });
                          } catch (err) {
                            logger.warn("[BookingTypeSelector] hover log failed", err);
                          }
                        };

                        const aggregateStatus = (() => {
                          if (!hasInstanceStatus) return { label: null, color: null };
                          const priority = { "Fully booked": 3, Unavailable: 3, Partial: 2, Available: 1, Unknown: 0 };
                          return instanceStatuses.reduce(
                            (best, { status }) => {
                              const label = status?.label || "Unknown";
                              const color = status?.color || "bg-gray-400";
                              const score = priority[label] ?? 0;
                              return score > best.score ? { label, color, score } : best;
                            },
                            { label: null, color: null, score: -1 }
                          );
                        })();

                        const dayStatus = getDayStatus(date); // full | partial | null

                        const dayTitle = [
                          aggregateStatus?.label ? `Status: ${aggregateStatus.label}` : null,
                          isBooked ? `${bookings.length} booking${bookings.length > 1 ? "s" : ""}` : null,
                        ]
                          .filter(Boolean)
                          .join(" • ");

                        const dayNumber = (
                          <div
                            className="w-full h-full flex flex-col items-center justify-center gap-1"
                            title={dayTitle || undefined}
                            onMouseEnter={handleHover}
                          >
                            <span
                              className={cn(
                                dayStatus === "full" && "text-red-600 font-semibold",
                                dayStatus === "partial" && "text-orange-600 font-semibold"
                              )}
                            >
                              {date.getDate()}
                            </span>
                            {aggregateStatus?.color && (
                              <span className={`h-2 w-2 rounded-full ${aggregateStatus.color}`} />
                            )}
                            {!aggregateStatus?.color && dayStatus && (
                              <span
                                className={cn(
                                  "h-2 w-2 rounded-full",
                                  dayStatus === "full" ? "bg-red-500" : "bg-orange-400"
                                )}
                              />
                            )}
                          </div>
                        );

                        // Always show tooltip with instance info
                        return (
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>{dayNumber}</TooltipTrigger>
                              <TooltipContent className="w-72 z-[9999]" side="top">
                                <div className="space-y-2">
                                  {isBooked && (
                                    <div className="text-sm">
                                      <p className="font-medium">
                                        {bookings.length} booking{bookings.length > 1 ? "s" : ""}
                                      </p>
                                      {bookings.slice(0, 2).map((booking, idx) => (
                                        <p key={idx} className="text-xs">
                                          {formatDate(new Date(booking.startTime), "HH:mm")} -{" "}
                                          {formatDate(new Date(booking.endTime), "HH:mm")}
                                        </p>
                                      ))}
                                      {bookings.length > 2 && (
                                        <p className="text-xs">... and {bookings.length - 2} more</p>
                                      )}
                                    </div>
                                  )}

                                  {hasInstanceStatus && (
                                    <div className="space-y-1">
                                      <p className="text-xs text-gray-500 font-medium">Availability</p>
                                      <ScrollableInstanceStatusList instanceStatuses={instanceStatuses} />
                                    </div>
                                  )}

                                  {!isBooked && !hasInstanceStatus && (
                                    <p className="text-xs text-gray-500">No availability info</p>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      },
                    }}
                  />
                </TooltipProvider>
              </PopoverContent>
            </Popover>
            {instances?.length > 0 && (
              <div className="flex items-center gap-3 text-xs text-gray-600 mt-2">
                <span className="text-blue-600">Hover the building icon to see availability.</span>
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm font-semibold text-gray-900">Months to reserve</Label>
            <Select
              disabled={disabled}
              value={monthlyMonthCount.toString()}
              onValueChange={value => setMonthlyMonthCount(parseInt(value, 10))}
            >
              <SelectTrigger className="w-full h-10 mt-2">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: Math.max(1, maximumDuration - minimumDuration + 1) }).map((_, idx) => {
                  const value = minimumDuration + idx;
                  return (
                    <SelectItem key={value} value={value.toString()}>
                      {value} {value === 1 ? "month" : "months"}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
            <div className="flex justify-between">
              <div>
                <span className="text-xs text-blue-700">Start</span>
                <div className="font-semibold">
                  {monthlyStartDate ? formatDate(monthlyStartDate, "MMM dd, yyyy") : "Select a start date"}
                </div>
              </div>
              <div>
                <span className="text-xs text-blue-700">End</span>
                <div className="font-semibold">
                  {monthlyEndDate ? formatDate(monthlyEndDate, "MMM dd, yyyy") : "Auto-set after start"}
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs">
              End date is automatically set {monthlyMonthCount * 30} days from your start date.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-900">
            Select Date Range
            <span className="ml-1 text-xs font-normal text-gray-500">
              (min {minimumDuration} {durationUnit})
            </span>
          </Label>
          <div className="flex items-center gap-2">
            <SmartDatePicker
              value={datePickerValue}
              onChange={handleSmartDatePickerChange}
              placeholder="Tap to pick dates"
              disabled={disabled}
              existingBookings={filteredBookings}
              availableDates={availableDatesSet}
              maxSelectableDate={maxSelectableDate}
              minDate={minSelectableDate}
              className="flex-1 flex justify-between items-center px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-blue-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm font-medium text-gray-900"
            />
            {instances?.length > 0 && renderInstancePreview(getPreviewDateForSmartPicker(datePickerValue))}
          </div>
          <p className="text-xs text-gray-500">
            {instances?.length > 0 && (
              <span className="text-blue-600">Hover the building icon to see availability.</span>
            )}
          </p>
        </div>
      )}

      {/* Show conflict warning if any */}
      {monthlyConflictWarning || rangeConflictWarning}

      {/* Time selection for DAILY bookings - HIDDEN as per user request */}
      {/* {durationType === "DAILY" && dateRange.from && dateRange.to && (
        ...
      )} */}
    </div>
  );
};

export default BookingTypeSelector;
